// E.164 phone number validation regex
const isValidE164 = (phone: string) => {
  if (!phone) return true; // allow empty
  return /^\+?[1-9]\d{1,14}$/.test(phone);
};
import React, { useEffect, useState } from "react";
import ApiClient from "../services/fuelandmoreapi";
import BinIcon from "./BinIcon";
import { config } from "../config";
import type { TUser } from "@/types";
import "../global.css";
import "./AdminUserList.css";
import { FeedbackText } from "./FeedbackText";
import UserForm from "./UserForm";

const AdminUserList: React.FC = () => {
  const [users, setUsers] = useState<TUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // unified modal for add/edit
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<TUser | null>(null); // user being edited or added
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [apiUrl]);

  const fetchUsers = async () => {
    setError(null);

    setLoading(true);

    try {
      const apiClient = new ApiClient(apiUrl);
      const response = await apiClient.getAdminUsers();
      setUsers(response);
    } catch (err) {
      setError("Failed to fetch admin users.");
    } finally {
      setLoading(false);
    }
  };

  // Removed unused handleUserAdded

  const handleDelete = async (email: string) => {
    setConfirmDelete(null);

    try {
      const deleteResponse = await new ApiClient(apiUrl).deleteAdminUser(email);
      if (deleteResponse.status < 200 || deleteResponse.status >= 300) {
        setError(
          deleteResponse.data?.message || "Failed to delete admin user."
        );
        return;
      }
      fetchUsers();
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to delete admin user.");
      }
    }
  };

  if (loading) {
    return <div>Loading admin users...</div>;
  }

  return (
    <div className="user-list-container">
      <h3 className="user-list-title">Admin Users</h3>

      <table className="user-list-table">
        <thead>
          <tr>
            <th>Given Name</th>
            <th>Family Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Job Title</th>
            <th>Company</th>
            <th>Country</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.email}>
              <td>{user.givenName}</td>
              <td>{user.familyName}</td>
              <td>{user.email}</td>
              <td>{user.phoneNumber}</td>
              <td>{user.jobTitle}</td>
              <td>{user.company}</td>
              <td>{user.country}</td>
              <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                <button
                  className="edit-btn"
                  title="Edit Admin User"
                  onClick={() => {
                    setEditUser(user);
                    setIsEditMode(true);
                    setShowModal(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "black",
                    display: "inline-block",
                    margin: "0 auto",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 17.25V21h3.75l11.06-11.06a1 1 0 0 0 0-1.41l-2.24-2.24a1 1 0 0 0-1.41 0L3 17.25z"
                      stroke="black"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M14.06 7.94l2.24 2.24"
                      stroke="black"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </td>
              <td style={{ textAlign: "center" }}>
                <button
                  className="bin-btn"
                  title="Delete Admin User"
                  onClick={() => setConfirmDelete(user.email)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <BinIcon color="black" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="add-user-button-container">
        <button
          className="primary-button"
          onClick={() => {
            setShowModal(true);
            setIsEditMode(false);
            setEditUser({
              givenName: "",
              familyName: "",
              email: "",
              phoneNumber: "",
              jobTitle: "",
              company: "",
              country: "",
              whatsAppId: "",
              appSource: undefined,
            });
          }}
        >
          + Add Admin User
        </button>
      </div>

      {/* UserForm modal for add/edit */}
      {showModal && editUser && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ width: "480px", maxWidth: "95%" }}
          >
            <h4>{isEditMode ? "Edit Admin User" : "Add Admin User"}</h4>
            <UserForm
              initialUser={editUser}
              mode={isEditMode ? "edit" : "add"}
              loading={editLoading}
              error={error}
              disableEmail={
                isEditMode &&
                editUser.email !== "" &&
                users.some((u) => u.email === editUser.email)
              }
              onSave={async (user) => {
                setEditLoading(true);
                setError(null);
                try {
                  const apiClient = new ApiClient(apiUrl);
                  if (!isEditMode) {
                    // Add
                    const response = await apiClient.createAdminUser(user);
                    if (response.status < 200 || response.status >= 300) {
                      setError(
                        response.data?.message || "Failed to add admin user."
                      );
                    } else {
                      const updatedUsers = await apiClient.getAdminUsers();
                      setUsers(updatedUsers);
                      setShowModal(false);
                      setEditUser(null);
                    }
                  } else {
                    // Edit
                    const response = await apiClient.updateAdminUser(user);
                    if (response.status < 200 || response.status >= 300) {
                      setError(
                        response.data?.message || "Failed to update admin user."
                      );
                    } else {
                      const updatedUsers = await apiClient.getAdminUsers();
                      setUsers(updatedUsers);
                      setShowModal(false);
                      setEditUser(null);
                    }
                  }
                } catch (err) {
                  setError(
                    !isEditMode
                      ? "Failed to add admin user."
                      : "Failed to update admin user."
                  );
                } finally {
                  setEditLoading(false);
                }
              }}
              onCancel={() => {
                setShowModal(false);
                setEditUser(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete admin user <b>{confirmDelete}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDelete(confirmDelete!)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;
